import React, { useState, useEffect, useRef, useCallback } from "react";
import * as classes from "./flowStepPhoneVerify.module.scss";
import { useForm } from "react-hook-form";
import TextInput from "@components/ui/textInput";
import SubmitButton from "@components/ui/submitButton";
import FlowStepContainer from "./flowStepContainer";

export default function FlowStepPhoneVerify({ stepNo, totalSteps, phone, onVerified, onTryAgain, isDev = false }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    getValues,
    setValue,
    setFocus,
    reset,
  } = useForm();
  const digitInputs = [useRef(), useRef(), useRef(), useRef()];
  const submitBtn = useRef();

  const focusFirst = useCallback(() => {
    const timeout = setTimeout(() => {
      setFocus(`verify.0`);
    }, 500);
    return () => clearTimeout(timeout);
  }, [setFocus]);

  const digitsOnly = (input) => input && input.replace(/[^\d]/g, "");
  const pasteDigits = (e) => digitsOnly((e.clipboardData || window.clipboardData).getData("text"));

  const onSubmit = () => {
    const inputCode = getValues().verify.join("");
    if (inputCode !== code) {
      reset();
      setError("code", { type: "value" });
      focusFirst();
    } else {
      onVerified({ hasVerifiedPhone: true });
    }
  };

  const [code] = useState(
    Array(4)
      .fill()
      .map(() => Math.floor(Math.random() * 9))
      .join("")
  );
  useEffect(() => {
    if (isDev) {
      console.info("Phone verification code:", code);
    }

    const sendCode = () =>
      fetch(`/api/verify`, {
        method: `POST`,
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          phone: phone.replace(/\s/g, ""),
          code,
        }),
      });
    sendCode();
  }, [phone]);

  useEffect(() => {
    focusFirst();
  }, [focusFirst]);

  const bumpFocus = (idx) => {
    if (idx < 3) setFocus(`verify.${idx + 1}`);
    else submitBtn.current.click();
  };

  const onChange = (idx) => (e) => {
    const value = digitsOnly(e.target.value);
    if (value && value.length === 1) {
      setValue(`verify.${idx}`, digitsOnly(e.target.value));
      bumpFocus(idx);
    }
  };
  const onPaste = (e) => {
    Array.from(pasteDigits(e)).forEach((d, i) => {
      setValue(`verify.${i}`, d);
      bumpFocus(i);
    });
  };
  const DigitVerify = ({ idx }) => {
    const [focused, setFocused] = React.useState(false);
    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);
    const field = register(`verify.${idx}`, {
      onChange: onChange(idx),
    });

    return (
      <TextInput
        className={classes.digit}
        tabIndex={idx}
        type="text"
        name={`verify.${idx}`}
        required="required"
        onPaste={onPaste}
        {...field}
        autoComplete="one-time-code"
        placeholder={focused ? "" : "*"}
        errors={errors}
        onFocus={onFocus}
        onBlur={onBlur}
        v2={true}
      />
    );
  };

  return (
    <FlowStepContainer
      stepNo={stepNo}
      totalSteps={totalSteps}
      title="Verify your phone number"
      innerCtnClass={classes.inner}
    >
      <div className={classes.subtitle}>
        Enter the 4-digit code we sent to <span className={classes.phone}>{phone}</span>
      </div>
      {errors.code && <div className={classes.error}>Invalid code, please try again.</div>}
      <form onSubmit={handleSubmit(onSubmit, onSubmit)}>
        <div className={classes.digits}>
          <DigitVerify idx={0} verifyRef={digitInputs[0]} />
          <DigitVerify idx={1} verifyRef={digitInputs[1]} />
          <DigitVerify idx={2} verifyRef={digitInputs[2]} />
          <DigitVerify idx={3} verifyRef={digitInputs[3]} />
        </div>
        <SubmitButton ref={submitBtn} tabIndex={5} className={classes.btn} id={"phone-verify-submit"}>
          Submit
        </SubmitButton>
      </form>
      <div className={classes.again}>
        Didn't get a code? <a onClick={() => onTryAgain()}>Try again</a>
      </div>
    </FlowStepContainer>
  );
}
